import Card from '../../../components/commoncomponents/Card';
import Table from '../../../components/commoncomponents/Table';

function DashboardSummary({ businesses }) {
  const totalBusinesses = businesses.length;
  const totalBranches = businesses.reduce(
    (accumulator, business) => accumulator + Number(business.branch_count || 0),
    0
  );
  const activeBusinesses = businesses.filter((item) => item.status === 'active').length;
  const underConstructionBusinesses = businesses.filter(
    (item) => item.status === 'under_construction'
  ).length;
  const lockedBusinesses = businesses.filter(
    (item) => (item.portal_access ?? item.portal_state) === 'locked'
  ).length;
  const openBusinesses = businesses.filter(
    (item) => (item.portal_access ?? item.portal_state) === 'open'
  ).length;
  const businessesNeedingAttention = businesses
    .filter(
      (item) =>
        item.status !== 'active' || (item.portal_access ?? item.portal_state) !== 'open'
    )
    .sort((left, right) => right.id - left.id)
    .slice(0, 4)
    .map((business) => ({
      id: business.id,
      name: business.name,
      status: business.status,
      portal_access: business.portal_access ?? business.portal_state ?? 'open',
      branch_count: Number(business.branch_count || 0),
    }));

  const businessTypeCounts = businesses.reduce((accumulator, business) => {
    const key = business.system_type || 'unassigned';
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});

  const topBusinessTypes = Object.entries(businessTypeCounts)
    .map(([name, count]) => ({
      id: name,
      name,
      count,
    }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 4);

  const recentBusinesses = [...businesses]
    .sort((left, right) => right.id - left.id)
    .slice(0, 5)
    .map((business, index) => ({
      id: business.id,
      number: index + 1,
      name: business.name,
      system_type: business.system_type,
      status: business.status,
      portal_access: business.portal_access ?? business.portal_state ?? 'open',
    }));

  const summaryCards = [
    { id: 1, label: 'Total Businesses', value: totalBusinesses, detail: 'Registered tenants' },
    { id: 2, label: 'Total Branches', value: totalBranches, detail: 'Branch locations across businesses' },
    {
      id: 3,
      label: 'Active Businesses',
      value: activeBusinesses,
      detail: 'Currently operational',
    },
    {
      id: 4,
      label: 'Open Portal Access',
      value: openBusinesses,
      detail: 'Ready for tenant login',
    },
  ];

  const recentBusinessColumns = [
    { key: 'number', label: 'No.' },
    { key: 'name', label: 'Business Name' },
    { key: 'system_type', label: 'System Type' },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
            row.status === 'active'
              ? 'bg-[#edf7ef] text-[#4e7b56]'
              : row.status === 'under_construction'
                ? 'bg-[#fff4df] text-[#a36a18]'
                : 'bg-[#f5f2ee] text-[#93857b]'
          }`}
        >
          {row.status.replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'portal_access',
      label: 'Portal Access',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
            row.portal_access === 'open'
              ? 'bg-[#edf7ef] text-[#4e7b56]'
              : row.portal_access === 'blurred'
                ? 'bg-[#f2efff] text-[#6657a8]'
                : 'bg-[#f4efea] text-[#7d6858]'
          }`}
        >
          {row.portal_access}
        </span>
      ),
    },
  ];

  return (
    <div className="grid gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((stat) => (
          <Card className="p-6" key={stat.id}>
            <p className="m-0 text-sm text-[#8b7a6d]">{stat.label}</p>
            <strong className="my-3 block text-[2rem] text-[#111111]">{stat.value}</strong>
            <span className="text-[#8b7a6d]">{stat.detail}</span>
          </Card>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)]">
        <Card className="p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="m-0 text-xl font-semibold text-[#111111]">Recent Businesses</h3>
            <p className="mt-2 text-sm text-[#8b7a6d]">
              Newest tenant businesses added to the platform.
            </p>
          </div>

          <div className="min-h-[320px]">
            <Table
              columns={recentBusinessColumns}
              rows={recentBusinesses}
              emptyState={{
                title: 'No businesses yet',
                message: 'Once a business is created in Business List, it will appear here too.',
                showAction: false,
              }}
            />
          </div>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="m-0 text-xl font-semibold text-[#111111]">Business Type Overview</h3>
            <p className="mt-2 text-sm text-[#8b7a6d]">
              Quick count of the most-used system types across businesses.
            </p>
          </div>

          <div className="grid gap-3">
            {topBusinessTypes.length ? (
              topBusinessTypes.map((item) => (
                <div
                  className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4"
                  key={item.id}
                >
                  <div>
                    <p className="text-sm font-semibold capitalize text-[#111111]">{item.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.08em] text-[#93857b]">
                      System Type
                    </p>
                  </div>
                  <span className="text-2xl font-semibold text-[#111111]">{item.count}</span>
                </div>
              ))
            ) : (
              <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-[#e6ddd4] bg-[#fcfbf9] px-6 text-center text-sm text-[#93857b]">
                Business type distribution will appear once businesses are added.
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(280px,0.9fr)_minmax(0,1.1fr)]">
        <Card className="p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="m-0 text-xl font-semibold text-[#111111]">Platform Readiness</h3>
            <p className="mt-2 text-sm text-[#8b7a6d]">
              Quick health check of build progress and access state.
            </p>
          </div>

          <div className="grid gap-3">
            <div className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
              <div>
                <p className="text-sm font-semibold text-[#111111]">Under Construction</p>
                <p className="mt-1 text-xs uppercase tracking-[0.08em] text-[#93857b]">
                  Businesses still in progress
                </p>
              </div>
              <span className="text-2xl font-semibold text-[#111111]">{underConstructionBusinesses}</span>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
              <div>
                <p className="text-sm font-semibold text-[#111111]">Locked Access</p>
                <p className="mt-1 text-xs uppercase tracking-[0.08em] text-[#93857b]">
                  Visible but not yet accessible
                </p>
              </div>
              <span className="text-2xl font-semibold text-[#111111]">{lockedBusinesses}</span>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="m-0 text-xl font-semibold text-[#111111]">Needs Attention</h3>
            <p className="mt-2 text-sm text-[#8b7a6d]">
              Businesses that are not fully active and open yet.
            </p>
          </div>

          <div className="grid gap-3">
            {businessesNeedingAttention.length ? (
              businessesNeedingAttention.map((business) => (
                <div
                  className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4"
                  key={business.id}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-[#111111]">{business.name}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.08em] text-[#93857b]">
                        {business.branch_count} branch{business.branch_count === 1 ? '' : 'es'}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] ${
                          business.status === 'under_construction'
                            ? 'bg-[#fff4df] text-[#a36a18]'
                            : 'bg-[#f5f2ee] text-[#93857b]'
                        }`}
                      >
                        {business.status.replace('_', ' ')}
                      </span>
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] ${
                          business.portal_access === 'blurred'
                            ? 'bg-[#f2efff] text-[#6657a8]'
                            : business.portal_access === 'locked'
                              ? 'bg-[#f4efea] text-[#7d6858]'
                              : 'bg-[#edf7ef] text-[#4e7b56]'
                        }`}
                      >
                        {business.portal_access}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-[#e6ddd4] bg-[#fcfbf9] px-6 text-center text-sm text-[#93857b]">
                All businesses are currently active and open.
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default DashboardSummary;
